import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Tour step images - using inline SVG for reliability
const tourImages = {
  phases: `<svg viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="400" height="200" fill="#1e293b"/>
    <rect x="20" y="20" width="360" height="40" rx="6" fill="#334155"/>
    <text x="30" y="45" fill="#fff" font-size="14" font-weight="bold">Phases Overview</text>
    <rect x="20" y="70" width="360" height="30" rx="4" fill="#3b82f6"/>
    <text x="30" y="90" fill="#fff" font-size="12">Foundation - $25,000</text>
    <rect x="340" y="77" width="30" height="16" rx="2" fill="#22c55e"/>
    <text x="345" y="89" fill="#fff" font-size="9">Paid</text>
    <rect x="20" y="105" width="360" height="30" rx="4" fill="#334155"/>
    <text x="30" y="125" fill="#fff" font-size="12">Framing - $45,000</text>
    <rect x="20" y="140" width="360" height="30" rx="4" fill="#334155"/>
    <text x="30" y="160" fill="#fff" font-size="12">Electrical - $18,000</text>
  </svg>`,
  phaseDetails: `<svg viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="400" height="200" fill="#1e293b"/>
    <rect x="20" y="20" width="360" height="60" rx="8" fill="#334155"/>
    <text x="30" y="45" fill="#60a5fa" font-size="13" font-weight="bold">Foundation & Utilities</text>
    <text x="300" y="45" fill="#94a3b8" font-size="12">$35,000</text>
    <rect x="30" y="55" width="60" height="20" rx="4" fill="#475569" stroke="#60a5fa" stroke-width="2"/>
    <text x="40" y="69" fill="#fff" font-size="10">Details</text>
    <circle cx="110" cy="65" r="8" fill="#3b82f6"/>
    <text x="107" y="69" fill="#fff" font-size="10">i</text>
    <rect x="280" y="55" width="80" height="20" rx="4" fill="#f59e0b"/>
    <text x="285" y="69" fill="#fff" font-size="9">Under Review</text>
    <text x="130" y="120" fill="#94a3b8" font-size="11">👆 Click Details to view & edit</text>
    <rect x="20" y="140" width="360" height="40" rx="6" fill="#1e40af"/>
    <text x="100" y="165" fill="#fff" font-size="12">View costs, timeline, contractor & edit phase</text>
  </svg>`,
  timeline: `<svg viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="400" height="200" fill="#1e293b"/>
    <text x="20" y="30" fill="#94a3b8" font-size="10">Jan</text>
    <text x="90" y="30" fill="#94a3b8" font-size="10">Feb</text>
    <text x="160" y="30" fill="#94a3b8" font-size="10">Mar</text>
    <text x="230" y="30" fill="#94a3b8" font-size="10">Apr</text>
    <text x="300" y="30" fill="#94a3b8" font-size="10">May</text>
    <text x="370" y="30" fill="#94a3b8" font-size="10">Jun</text>
    <rect x="20" y="50" width="120" height="24" rx="4" fill="#22c55e"/>
    <text x="30" y="66" fill="#fff" font-size="10">Foundation</text>
    <rect x="100" y="80" width="150" height="24" rx="4" fill="#f59e0b"/>
    <text x="110" y="96" fill="#fff" font-size="10">Framing</text>
    <rect x="200" y="110" width="100" height="24" rx="4" fill="#3b82f6"/>
    <text x="210" y="126" fill="#fff" font-size="10">Electrical</text>
    <rect x="280" y="140" width="90" height="24" rx="4" fill="#3b82f6"/>
    <text x="290" y="156" fill="#fff" font-size="10">Plumbing</text>
    <line x1="180" y1="40" x2="180" y2="180" stroke="#ef4444" stroke-width="2" stroke-dasharray="4"/>
    <text x="185" y="175" fill="#ef4444" font-size="9">Today</text>
  </svg>`,
  loanDetails: `<svg viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="400" height="200" fill="#1e293b"/>
    <rect x="20" y="20" width="170" height="70" rx="8" fill="#334155"/>
    <text x="30" y="45" fill="#94a3b8" font-size="10">Loan Amount</text>
    <text x="30" y="70" fill="#fff" font-size="18" font-weight="bold">$450,000</text>
    <rect x="210" y="20" width="170" height="70" rx="8" fill="#334155"/>
    <text x="220" y="45" fill="#94a3b8" font-size="10">Available for Draw</text>
    <text x="220" y="70" fill="#22c55e" font-size="18" font-weight="bold">$125,000</text>
    <rect x="20" y="110" width="360" height="40" rx="6" fill="#3b82f6"/>
    <text x="140" y="135" fill="#fff" font-size="14" font-weight="bold">+ New Draw Request</text>
    <rect x="20" y="160" width="360" height="30" rx="4" fill="#334155"/>
    <text x="30" y="180" fill="#94a3b8" font-size="11">Interest accrued: $3,245.00</text>
  </svg>`,
  bidSheet: `<svg viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="400" height="200" fill="#1e293b"/>
    <text x="20" y="30" fill="#fff" font-size="14" font-weight="bold">Construction Bid Sheet</text>
    <rect x="20" y="45" width="360" height="25" rx="4" fill="#1e40af"/>
    <text x="30" y="62" fill="#fff" font-size="10">Category</text>
    <text x="150" y="62" fill="#fff" font-size="10">Description</text>
    <text x="310" y="62" fill="#fff" font-size="10">Est. Cost</text>
    <rect x="20" y="75" width="360" height="25" rx="2" fill="#334155"/>
    <text x="30" y="92" fill="#fff" font-size="10">Foundation</text>
    <text x="150" y="92" fill="#fff" font-size="10">Concrete work</text>
    <text x="310" y="92" fill="#fff" font-size="10">$25,000</text>
    <rect x="20" y="105" width="360" height="25" rx="2" fill="#1e293b"/>
    <text x="30" y="122" fill="#fff" font-size="10">Framing</text>
    <text x="150" y="122" fill="#fff" font-size="10">Structure</text>
    <text x="310" y="122" fill="#fff" font-size="10">$45,000</text>
    <rect x="20" y="160" width="360" height="30" rx="4" fill="#1e40af"/>
    <text x="280" y="180" fill="#fff" font-size="12" font-weight="bold">Total: $280,000</text>
  </svg>`,
  contractorAdd: `<svg viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="400" height="200" fill="#1e293b"/>
    <text x="20" y="30" fill="#fff" font-size="14" font-weight="bold">Add Contractor</text>
    <rect x="20" y="45" width="360" height="35" rx="6" fill="#334155"/>
    <text x="30" y="67" fill="#94a3b8" font-size="11">Company Name</text>
    <text x="150" y="67" fill="#fff" font-size="11">ABC Electric LLC</text>
    <rect x="20" y="85" width="175" height="35" rx="6" fill="#334155"/>
    <text x="30" y="107" fill="#94a3b8" font-size="11">Trade</text>
    <text x="100" y="107" fill="#fff" font-size="11">Electrical</text>
    <rect x="205" y="85" width="175" height="35" rx="6" fill="#334155"/>
    <text x="215" y="107" fill="#94a3b8" font-size="11">Phone</text>
    <text x="260" y="107" fill="#fff" font-size="11">555-1234</text>
    <rect x="20" y="125" width="360" height="35" rx="6" fill="#334155"/>
    <text x="30" y="147" fill="#94a3b8" font-size="11">Email</text>
    <text x="80" y="147" fill="#fff" font-size="11">contact@abcelectric.com</text>
    <rect x="250" y="165" width="130" height="30" rx="6" fill="#3b82f6"/>
    <text x="275" y="185" fill="#fff" font-size="12" font-weight="bold">+ Add Contractor</text>
  </svg>`,
  contractorInvite: `<svg viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="400" height="200" fill="#1e293b"/>
    <rect x="20" y="20" width="360" height="50" rx="8" fill="#334155"/>
    <circle cx="50" cy="45" r="18" fill="#3b82f6"/>
    <text x="45" y="50" fill="#fff" font-size="14" font-weight="bold">A</text>
    <text x="80" y="40" fill="#fff" font-size="13" font-weight="bold">ABC Electric LLC</text>
    <text x="80" y="55" fill="#94a3b8" font-size="10">Electrical • contact@abcelectric.com</text>
    <rect x="290" y="30" width="80" height="28" rx="6" fill="#22c55e"/>
    <text x="298" y="48" fill="#fff" font-size="9" font-weight="bold">Invite to Portal</text>
    <rect x="20" y="85" width="360" height="80" rx="8" fill="#1e40af" opacity="0.3"/>
    <text x="40" y="110" fill="#fff" font-size="11">📧 Email sent to contractor!</text>
    <text x="40" y="130" fill="#94a3b8" font-size="10">They'll receive a link to create their account</text>
    <text x="40" y="150" fill="#94a3b8" font-size="10">and access your projects.</text>
  </svg>`,
  contractorPortal: `<svg viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="400" height="200" fill="#1e293b"/>
    <text x="20" y="25" fill="#fff" font-size="12" font-weight="bold">Contractor Portal - What They See</text>
    <rect x="20" y="40" width="110" height="70" rx="6" fill="#334155"/>
    <text x="35" y="60" fill="#22c55e" font-size="20">✓</text>
    <text x="35" y="80" fill="#fff" font-size="9">Submit Bids</text>
    <text x="35" y="95" fill="#94a3b8" font-size="8">Respond to requests</text>
    <rect x="145" y="40" width="110" height="70" rx="6" fill="#334155"/>
    <text x="160" y="60" fill="#3b82f6" font-size="20">📄</text>
    <text x="160" y="80" fill="#fff" font-size="9">Upload W-9/COI</text>
    <text x="160" y="95" fill="#94a3b8" font-size="8">Compliance docs</text>
    <rect x="270" y="40" width="110" height="70" rx="6" fill="#334155"/>
    <text x="285" y="60" fill="#f59e0b" font-size="20">✍️</text>
    <text x="285" y="80" fill="#fff" font-size="9">Sign Waivers</text>
    <text x="285" y="95" fill="#94a3b8" font-size="8">Lien waivers</text>
    <rect x="20" y="120" width="175" height="70" rx="6" fill="#334155"/>
    <text x="35" y="145" fill="#8b5cf6" font-size="20">📷</text>
    <text x="35" y="165" fill="#fff" font-size="9">Upload Photos</text>
    <text x="35" y="180" fill="#94a3b8" font-size="8">Completion proof</text>
    <rect x="205" y="120" width="175" height="70" rx="6" fill="#334155"/>
    <text x="220" y="145" fill="#ec4899" font-size="20">📊</text>
    <text x="220" y="165" fill="#fff" font-size="9">View Projects</text>
    <text x="220" y="180" fill="#94a3b8" font-size="8">Assigned work only</text>
  </svg>`
};

const tourSteps = [
  {
    id: 'welcome',
    title: 'Welcome to DrawReady! 👋',
    description: 'Everything You Need to Run Real Estate Projects - In One System. Let us show you around in under 2 minutes!',
    target: null,
    position: 'center',
    sidebarHighlight: null,
    navigateTo: '/dashboard',
    image: null
  },
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    description: 'This is your command center. See all your projects at a glance with budget status, progress tracking, and quick access to everything.',
    target: null,
    position: 'center',
    sidebarHighlight: 'dashboard',
    navigateTo: '/dashboard',
    image: null
  },
  {
    id: 'create-project',
    title: 'Create Your First Project',
    description: 'Click "+ New Project" to start. Enter the property address, budget, and loan details. That\'s it - you\'re ready to track!',
    target: '[data-testid="create-project-btn"]',
    position: 'center',
    sidebarHighlight: 'dashboard',
    navigateTo: '/dashboard',
    image: null
  },
  {
    id: 'project-hub-intro',
    title: '🏗️ Project Hub',
    description: 'The Project Hub is where you manage all project details: budget phases, bid sheets, contractors, documents, and draw requests. Let\'s explore it.',
    target: null,
    position: 'center',
    sidebarHighlight: 'project-hub',
    navigateTo: '/project-hub',
    image: null
  },
  {
    id: 'project-hub-budget',
    title: '💰 Budget & Phases',
    description: 'Track every dollar across 8 construction phases. Add materials, labor costs, and mark payments as you go. Everything syncs automatically.',
    target: null,
    position: 'center',
    sidebarHighlight: 'project-hub',
    navigateTo: '/project-hub?tab=budget',
    image: null
  },
  {
    id: 'project-hub-bidsheets',
    title: '📋 Bid Sheets',
    description: 'Create bid requests, send to contractors, compare responses side-by-side, and award jobs. AI can auto-map your costs to bank categories.',
    target: null,
    position: 'center',
    sidebarHighlight: 'project-hub',
    navigateTo: '/project-hub?tab=bidsheets',
    image: null
  },
  {
    id: 'project-hub-contractors',
    title: '👷 Contractors',
    description: 'Add and manage all your contractors here. Track their assigned work, payment history, and compliance documents (W-9, COI, lien waivers).',
    target: null,
    position: 'center',
    sidebarHighlight: 'project-hub',
    navigateTo: '/project-hub?tab=contractors',
    image: null
  },
  {
    id: 'contractor-invite',
    title: '📧 Invite to Contractor Portal',
    description: 'Click "Invite to Portal" next to any contractor. They\'ll receive an email with a link to create their account and access your projects.',
    target: null,
    position: 'center',
    sidebarHighlight: 'project-hub',
    navigateTo: '/project-hub?tab=contractors',
    image: tourImages.contractorInvite
  },
  {
    id: 'contractor-portal-preview',
    title: '🖥️ The Contractor Portal',
    description: 'This is what YOUR contractors see when they log in. They can view assigned projects, submit bids, upload documents, and sign waivers - all in one place.',
    target: null,
    position: 'center',
    sidebarHighlight: null,
    navigateTo: '/contractor-portal-preview',
    image: null
  },
  {
    id: 'contractor-portal-bids',
    title: '✍️ Contractors Submit Bids',
    description: 'When you send a bid request, contractors see it here. They can review the scope, upload their bid document, and submit - no email back-and-forth.',
    target: null,
    position: 'center',
    sidebarHighlight: null,
    navigateTo: '/contractor-portal-preview',
    image: null
  },
  {
    id: 'contractor-portal-compliance',
    title: '📄 Compliance Made Easy',
    description: 'Contractors upload their W-9, insurance certificates (COI), and sign lien waivers digitally. You get notified and everything is tracked automatically.',
    target: null,
    position: 'center',
    sidebarHighlight: null,
    navigateTo: '/contractor-portal-preview',
    image: tourImages.contractorPortal
  },
  {
    id: 'project-hub-documents',
    title: '📁 Documents',
    description: 'All project documents in one place: contracts, receipts, permits, photos. Upload, organize, and attach to draw requests.',
    target: null,
    position: 'center',
    sidebarHighlight: 'project-hub',
    navigateTo: '/project-hub?tab=documents',
    image: null
  },
  {
    id: 'reports',
    title: '📊 Reports',
    description: 'Generate professional reports for your bank, investors, or your own records. Budget summaries, payment histories, and compliance status - all exportable.',
    target: null,
    position: 'center',
    sidebarHighlight: 'reports',
    navigateTo: '/reports',
    image: null
  },
  {
    id: 'financial-analysis',
    title: '📈 Financial Analysis',
    description: 'Track your overall portfolio performance. See trends across all projects, analyze cash flow, and make data-driven decisions.',
    target: null,
    position: 'center',
    sidebarHighlight: 'financial-analysis',
    navigateTo: '/financial-analysis',
    image: null
  },
  {
    id: 'complete',
    title: 'You\'re Ready! 🎉',
    description: 'Start by creating a project and inviting your first contractor. Need help? Click "Take a Tour" anytime to see this again.',
    target: null,
    position: 'center',
    sidebarHighlight: 'dashboard',
    navigateTo: '/dashboard',
    image: null
  }
];

export const WelcomeTour = ({ isOpen, onClose, onComplete, onImportClick }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightStyle, setHighlightStyle] = useState({});

  const step = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;

  // Navigate to the page for this step
  useEffect(() => {
    if (!isOpen) return;
    
    if (step.navigateTo) {
      // Small delay to allow modal to render first
      const timer = setTimeout(() => {
        navigate(step.navigateTo);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, step.navigateTo, currentStep, navigate]);

  // Highlight sidebar items based on current step
  useEffect(() => {
    if (!isOpen) return;
    
    // Remove all previous highlights
    document.querySelectorAll('[data-sidebar-item]').forEach(el => {
      el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2', 'bg-blue-500/20', 'scale-105');
    });
    
    // Add highlight to current sidebar item
    if (step.sidebarHighlight) {
      const sidebarItem = document.querySelector(`[data-sidebar-item="${step.sidebarHighlight}"]`);
      if (sidebarItem) {
        sidebarItem.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2', 'bg-blue-500/20', 'scale-105');
        sidebarItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    
    return () => {
      // Cleanup on unmount
      document.querySelectorAll('[data-sidebar-item]').forEach(el => {
        el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2', 'bg-blue-500/20', 'scale-105');
      });
    };
  }, [isOpen, step.sidebarHighlight, currentStep]);

  useEffect(() => {
    if (!isOpen || !step.target) {
      setHighlightStyle({});
      return;
    }

    const updateHighlight = () => {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setHighlightStyle({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
        });
        
        // Scroll element into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    updateHighlight();
    window.addEventListener('resize', updateHighlight);
    
    return () => window.removeEventListener('resize', updateHighlight);
  }, [isOpen, step.target, currentStep]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
      onClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 z-[100]" onClick={handleSkip} />
      
      {/* Highlight Box */}
      {step.target && highlightStyle.width && (
        <div
          className="fixed z-[101] border-2 border-blue-500 rounded-lg pointer-events-none"
          style={{
            top: highlightStyle.top,
            left: highlightStyle.left,
            width: highlightStyle.width,
            height: highlightStyle.height,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
          }}
        />
      )}
      
      {/* Tour Card */}
      <div 
        className={`fixed z-[102] bg-white rounded-2xl shadow-2xl p-8 w-[560px] max-w-[95vw] max-h-[90vh] overflow-y-auto ${
          step.position === 'center' ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' : ''
        }`}
        style={step.position !== 'center' && highlightStyle.top ? {
          top: Math.min(
            Math.max(20, step.position === 'bottom' ? highlightStyle.top + highlightStyle.height + 20 : 
                 step.position === 'top' ? highlightStyle.top - 280 : 
                 highlightStyle.top),
            window.innerHeight - 360
          ),
          left: Math.min(
            Math.max(20, step.position === 'left' ? highlightStyle.left - 580 :
                  step.position === 'right' ? highlightStyle.left + highlightStyle.width + 20 :
                  Math.max(20, highlightStyle.left - 150)),
            window.innerWidth - 580
          ),
        } : undefined}
      >
        {/* Progress Dots */}
        <div className="flex justify-center gap-2 mb-6">
          {tourSteps.map((_, idx) => (
            <div
              key={idx}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                idx === currentStep ? 'bg-blue-600 scale-125' : 
                idx < currentStep ? 'bg-green-500' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 p-1"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Content */}
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-3">{step.title}</h3>
          
          {/* Image for steps with images */}
          {step.image && (
            <div 
              className="w-full h-48 rounded-xl overflow-hidden mb-5 bg-slate-800 border border-slate-700"
              dangerouslySetInnerHTML={{ __html: step.image }}
            />
          )}
          
          <p className="text-slate-600 text-lg leading-relaxed">{step.description}</p>
          
          {/* Import Button for migration step */}
          {step.showImportButton && onImportClick && (
            <div className="mt-6 space-y-4">
              <button
                onClick={() => {
                  onImportClick();
                  onClose();
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 text-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Import My Spreadsheet
              </button>
              <button
                onClick={handleNext}
                className="w-full text-slate-500 hover:text-slate-700 text-base py-2"
              >
                Skip — I'll start fresh
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="text-slate-500 hover:text-slate-700 text-base"
          >
            Skip Tour
          </Button>
          
          <div className="flex gap-3">
            {!isFirstStep && (
              <Button
                variant="outline"
                onClick={handlePrev}
                className="flex items-center gap-1 px-5 py-2 h-11 text-base"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </Button>
            )}
            
            <Button
              onClick={handleNext}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1 px-6 py-2 h-11 text-base font-semibold"
            >
              {isLastStep ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  Get Started
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* Step Counter */}
        <p className="text-center text-sm text-slate-400 mt-5">
          Step {currentStep + 1} of {tourSteps.length}
        </p>
      </div>
    </>
  );
};

// Hook to manage tour state
export const useTour = () => {
  const [showTour, setShowTour] = useState(false);
  const [hasSeenTour, setHasSeenTour] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem('drawready_tour_completed');
    if (seen) {
      setHasSeenTour(true);
    }
  }, []);

  const startTour = () => {
    setShowTour(true);
  };

  const completeTour = () => {
    localStorage.setItem('drawready_tour_completed', 'true');
    setHasSeenTour(true);
    setShowTour(false);
  };

  const closeTour = () => {
    setShowTour(false);
  };

  const resetTour = () => {
    localStorage.removeItem('drawready_tour_completed');
    setHasSeenTour(false);
  };

  return {
    showTour,
    hasSeenTour,
    startTour,
    completeTour,
    closeTour,
    resetTour
  };
};

export default WelcomeTour;
